import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';

/**
 * PROD-983 — empty-grid-cell quick-create (By-Doctor view).
 *
 * Flutter parity: `calendar_by_doctor_widget.dart:499-530` — double-tap an
 * empty cell → `dateFromPosition(dy)` → `onTapNewAppointmentRequest({slotStart,
 * doctor})` → `calendar_tab.dart:537-566` picks the preferred-or-first profile,
 * sets `slotEnd = slotStart + profile.minutesDuration`, and opens the create
 * panel pre-filled (`onQuickCreate`). Only By-Doctor has this in Flutter
 * (Week/Room widgets carry no such handler) — confirmed with Andrew 2026-06-03.
 *
 * These helpers are the pure (testable) core; the DOM geometry (click Y →
 * fraction-of-hour) lives in the component, and the panel wiring lives in
 * frame-scheduling's `handleQuickCreate`.
 */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local, no-`Z` ISO (`YYYY-MM-DDTHH:mm:ss`) — the wire form the backend's
 *  .NET model binder expects (a trailing `Z` is parsed wrong). Mirrors the
 *  shape `appointment.start` already arrives in. */
function toLocalNoZISO(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * Map a double-click inside an hour-row cell to a start time.
 *
 * @param selectedDate  the calendar's displayed day (date part is used).
 * @param hour          the hour the clicked row represents (e.g. 14 = 2pm).
 * @param fractionOfHour click Y offset within the cell / cell height, 0..1.
 * @param grainMin       snap grain in minutes (default 5) — avoids
 *                       sub-minute start times from pixel-precise clicks.
 * @returns local no-Z ISO start time.
 */
export function quickCreateStartISO(
  selectedDate: Date,
  hour: number,
  fractionOfHour: number,
  grainMin = 5,
): string {
  const frac = Math.max(0, Math.min(fractionOfHour, 0.999));
  const minutesIntoHour = Math.round((frac * 60) / grainMin) * grainMin;
  const total = Math.max(0, Math.min(hour * 60 + minutesIntoHour, 24 * 60 - grainMin));
  const d = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    0,
    0,
    0,
    0,
  );
  d.setMinutes(total);
  return toLocalNoZISO(d);
}

/**
 * Drag-to-reschedule (PROD-984): map a vertical drag delta (px) to a new
 * start time. Mirrors Flutter `newStartDateAfterDrag` (offset-based) — the
 * delta maps pixels→minutes against the row height, snapped to `grainMin`.
 * Returns null on a zero-minute shift (a no-op drag, e.g. a stray click that
 * crossed the activation distance) so the caller skips the reschedule.
 * Horizontal movement is irrelevant — reschedule is time-only.
 */
export function dragDeltaToNewStartISO(
  startISO: string,
  deltaY: number,
  hourHeight: number,
  grainMin = 5,
): string | null {
  // Reject a non-finite, zero, negative, OR sub-pixel (< 1px/hr) row height
  // (review §2): a tiny height blows up `deltaY / hourHeight` into a
  // nonsensical minute offset. A real grid row is ≥ 60px; anything < 1 is a
  // measurement bug, so no-op rather than silently emit a bad time.
  if (!Number.isFinite(hourHeight) || hourHeight < 1) return null;
  const minutesShift = Math.round((deltaY / hourHeight) * 60 / grainMin) * grainMin;
  if (minutesShift === 0) return null;
  return addMinutesISO(startISO, minutesShift);
}

/** Add `minutes` to a local no-Z ISO and return the same no-Z form. */
export function addMinutesISO(startISO: string, minutes: number): string {
  const [datePart, timePart = '00:00:00'] = startISO.split('T');
  const [y, m, day] = datePart.split('-').map(Number);
  const [hh, mm, ss = 0] = timePart.split(':').map(Number);
  const d = new Date(y, (m ?? 1) - 1, day ?? 1, hh ?? 0, mm ?? 0, ss ?? 0, 0);
  d.setMinutes(d.getMinutes() + minutes);
  return toLocalNoZISO(d);
}

/**
 * Preferred-or-first appointment profile from a provider's profile list —
 * mirrors Flutter `doc.appointmentProfile.where(isPreferred).first ?? .first`
 * (`calendar_tab.dart:541-550`).
 *
 * IMPORTANT (PR #427 review §2): this MUST run on the provider's RAW,
 * original-order `appointmentProfile` list — NOT the location-grouped
 * `quickBookGroups`, which `groupProfilesByLocation` re-sorts alphabetically
 * by `appointmentType.display` and regroups. Selecting from the regrouped
 * order would pick a different "first" than Flutter when no profile is
 * preferred (or break the tiebreak when two are). `find` already takes the
 * first match in iteration order, matching Dart's `.where(...).first`.
 * Returns null when the provider has no profiles.
 */
export function preferredProfileFromList(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): CreateAppointmentProfileRequest | null {
  if (!profiles || profiles.length === 0) return null;
  // Room-mitigation (2026-06-03): a provider can carry DUPLICATE profiles of
  // the same name where one has a usable room and the other has none. An
  // appointment booked with the room-less profile is un-openable +
  // un-reschedulable — the backend's `GetRoomByIdAsync` 500s/empties on a
  // missing/empty roomId (reproduces in Flutter too). So PREFER profiles that
  // carry a usable room; only fall back to the room-less set if NONE have a
  // room (so providers whose profiles are all room-less still get a default).
  // Within the chosen pool keep Flutter's `isPreferred ?? first` rule.
  const withRoom = profiles.filter((p) => hasRoomId(p.roomId));
  const pool = withRoom.length > 0 ? withRoom : profiles;
  // `isPreferred ?? first`: when no profile in the pool is preferred (all
  // `isPreferred: false`) this falls through to `pool[0]` — the deliberate
  // default. (review §5)
  return pool.find((p) => p.isPreferred === true) ?? pool[0];
}

/**
 * True when a roomId is merely PRESENT — a non-empty, non-all-zero GUID.
 *
 * NAME IS DELIBERATELY MODEST (review §4): this does NOT validate that the
 * room actually resolves. A profile can carry a non-empty but PHANTOM roomId
 * (a room id that no longer exists) and this still returns true — phantom
 * rooms are indistinguishable client-side (no field flags them; there is no
 * rooms-list endpoint to check against). It only catches the *missing* /
 * empty / all-zero cases. The phantom-duplicate case is handled best-effort by
 * `dedupeProfilesPreferRoom`; the robust fix is backend/data (room-fix ticket).
 */
export function hasRoomId(roomId: string | null | undefined): boolean {
  if (!roomId) return false;
  const t = roomId.trim();
  if (t === '' || t === '00000000-0000-0000-0000-000000000000') return false;
  return true;
}

/**
 * Defense-in-depth for the duplicate-profile / phantom-room bug (2026-06-03).
 *
 * A provider can carry DUPLICATE appointment profiles of the same
 * name+location — e.g. "First Appointment - Virtual" appears 3× for one
 * provider, where the live one points at a real room and the dead twins point
 * at a phantom room (`f43671d3`, which `GetRoomByIdAsync` can't resolve →
 * the appointment becomes un-openable/un-reschedulable; reproduces in Flutter
 * too). The dead twins are NOT distinguishable by any client field (same
 * non-empty roomId shape, all `isDefault`, no `isArchived`) and there is no
 * rooms-list endpoint to validate against. So the best the client can do is
 * collapse same-name/same-location duplicates to ONE entry, preferring a
 * room-bearing twin and otherwise keeping the FIRST (the backend orders the
 * canonical/current profile first in practice — order-dependent, hence
 * "best-effort"). `isPreferred` is inherited from any collapsed twin so the
 * downstream `isPreferred ?? first` selection doesn't resurrect the dead one.
 *
 * THE ROBUST FIX IS BACKEND/DATA (remove the dead duplicates / repoint the
 * phantom room / null-guard `GetRoomByIdAsync`). This only reduces how often
 * React books the broken twin.
 */
export function dedupeProfilesPreferRoom(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): CreateAppointmentProfileRequest[] {
  if (!profiles || profiles.length === 0) return [];
  const byKey = new Map<string, CreateAppointmentProfileRequest>();
  for (const p of profiles) {
    const locId = (p.location as { id?: string } | undefined)?.id ?? '';
    const key = `${p.profileName ?? ''}|${locId}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, p);
      continue;
    }
    // Inherit isPreferred from a collapsed twin so the kept (live) profile
    // wins the downstream `isPreferred ?? first` selection rather than the
    // dead-but-preferred twin being re-selected from the raw list elsewhere.
    // (If ALL twins are `isPreferred: false`, this stays false — downstream
    // falls through to first, which is correct. review §5)
    const inheritedPreferred = existing.isPreferred || p.isPreferred;
    // ⚠️ ORDER-DEPENDENT (review §2): when the kept twin already has a roomId,
    // we DON'T replace it — we keep whichever came FIRST. Both a live room and
    // a phantom room satisfy `hasRoomId` (phantoms are indistinguishable), so
    // correctness here RELIES ON the backend returning the canonical/live
    // profile first. If a dead twin ever sorts first, this keeps the dead one
    // (see the "dead twin sorts first" known-limitation test). Replacement only
    // upgrades a room-LESS kept entry to a room-bearing incoming one.
    const replace = !hasRoomId(existing.roomId) && hasRoomId(p.roomId);
    const kept = replace ? p : existing;
    byKey.set(key, { ...kept, isPreferred: inheritedPreferred });
  }
  return Array.from(byKey.values());
}
